import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';

import Consts from "Interfaces/Consts";
import { ContentShowProps as Props } from './ContentShow.interface';
import useContentShowLogic from './useContentShowLogic';

// Components
import ListView from "./ListViewComponent";
import ComponentHeader from "GeneralComponents/ComponentHeader";
import ComponentBackground from "GeneralComponents/ComponentBackground"
import ShowMoreButton from "GeneralComponents/ShowMoreButton";
import ComponentSearchAndFilter from 'GeneralComponents/SearchAndFilter/ComponentSearchAndFilter';
import Pagination from "GeneralComponents/Pagination";
import CustomLinkButton from 'GeneralComponents/CustomLinkButton';
import GenericErrorMsgComponent from 'GeneralComponents/GenericErrorMessage';

import "./ContentShow.min.css";
import Helper from '../../Interfaces/Helper';


const ContentShow: React.FC<Props> = (props) => {

    const { Title, Description, ComponentId, Query, CurrentPageId, ContentShowType, TextFilter, ShowFilters, CollageLayoutSize, ItemsNumberInRow, HideShowMoreButton, LinkTarget, TitleAlignment, ContentAlignment } = props;
    const ContentShowHook = useContentShowLogic(props);
    const st = ContentShowHook.STATE;
    const { changeShowMore, getItems, pagerCallback, searchCallback, setIsLightBG } = ContentShowHook.FUNC;
    const { LISTS, BackGround, dir, isCollage, itemsDisplayByViewType, limitOfItems, Link, mediaTypeLG } = ContentShowHook.VARS;


    // Get items on mount:
    useEffect(() => { getItems('', st.filterValues) }, []);

    if (!st.isLoaded) return null;

    // Display Config Vars:
    const noImagesClass = st.items.every(element => element.image.url === "") ? "noImages" : "";
    const hasPagination: boolean = (ContentShowType === LISTS.SQUARES) && (st.itemsCount > limitOfItems);
    const hideShowMoreButton: boolean = HideShowMoreButton?.toLowerCase() === "true";
    const hasShowMoreBtn: boolean = !hideShowMoreButton && (ContentShowType !== LISTS.MESSAGES) && (ContentShowType != LISTS.SQUARES) && (ContentShowType !== LISTS.CIRCLES) && (st.items.length > itemsDisplayByViewType);
    const hasSearchComponent: boolean = (Query !== "0") && (ContentShowType !== LISTS.DYNAMIC) && (ContentShowType !== LISTS.CIRCLES) && !isCollage;
    const hasCustomLinkBtn: boolean = (ContentShowType === LISTS.MESSAGES) && !!Link.url;
    const hasActiveQuerySearch = !!st.searchTerm || st.filterValues.some(f => !!f);
    const isIconView = (st.items && st.itemsCount > 0) && !st.items[0].image.url;
    const linkTarget = Helper.getLinkTarget(LinkTarget);

    return (

        <ComponentBackground
            componentId={"ContentShow-" + ComponentId}
            bgImage=""
            bgMobileImage=""
            bgImgOpacity=""
            backGround={BackGround}
            setLightBg={setIsLightBG}
            viewName={ContentShowType}>

            <article>

                <div className="container">

                    <ComponentHeader
                        disptitle={Title}
                        dispdescription={Description}
                        light={st.isLightBG}
                        alignment={Helper.getAlignment(TitleAlignment)}
                    />

                    {
                        hasSearchComponent &&

                        <ComponentSearchAndFilter
                            hasSearchInput={TextFilter !== null}
                            showFilters={ShowFilters !== null}
                            totalSearchResults={st.itemsCount}
                            currentPageId={CurrentPageId}
                            componentType={ContentShowType}
                            isCalledFromContentShow={true}
                            callbackFromComponentSearch={searchCallback}
                            disptitle={Title}
                            componentId={ComponentId}
                            filters={st.filters}
                        />

                    }

                    <ListView
                        CollageLayoutSize={CollageLayoutSize}
                        ContentShowType={ContentShowType}
                        componentId={"ContentShow-" + ComponentId}
                        currentPageId={CurrentPageId}
                        description={Description}
                        title={Title}
                        items={st.items}
                        dir={dir}
                        noImagesClass={noImagesClass}
                        iconView={isIconView}
                        isShowMore={st.isShowMore}
                        hideShowMoreButton={hideShowMoreButton}
                        itemsToShowOnLoad={!!hasShowMoreBtn ? (mediaTypeLG.matches ? 8 : 6) : 999}
                        itemsNumberInRow={!!hasShowMoreBtn || (ItemsNumberInRow != null && ItemsNumberInRow != "0") ? ItemsNumberInRow : "999"}
                        linkTarget={linkTarget}
                        showFilters={ShowFilters !== null}
                        filters={st.filters}
                        alignment={Helper.getAlignment(ContentAlignment)}
                    />

                    {
                        hasPagination &&

                        <div className="pt-4 pb-5 mst-ReactPaginate">
                            <Pagination
                                callbackFromPager={pagerCallback}
                                itemsPerPage={limitOfItems}
                                itemsCount={st.itemsCount}
                                renewPageNumber={st.renewPageNumber}
                                componentId={ComponentId}
                            />
                        </div>
                    }
                    <div className={`text-center ${isCollage ? "d-none" : ""}`}>
                        {
                            hasShowMoreBtn &&
                            <ShowMoreButton
                                showMore={changeShowMore}
                                btnClass={`${st.isLightBG ? 'btn-outline-primary' : 'btn-outline-light'} btn mst-btn px-5 mx-auto mt-4 mst-show-more-btn`}
                                txtOpen={`${Consts.showMore} (${st.items.length - itemsDisplayByViewType})`}
                                txtClose={Consts.close}
                                isOpen={st.isShowMore}
                            />
                        }
                    </div>
                    {st.items.length === 0 &&
                        <div className="px-3 px-lg-0 pb-3">

                            <GenericErrorMsgComponent
                                show={hasActiveQuerySearch && st.items.length === 0}
                                text={Consts.nosearchResult}
                                wrapperSpacingClass={'mx-0 mb-3'}
                            />

                        </div>
                    }

                    {
                        hasCustomLinkBtn &&

                        <div className="text-center py-5">
                            < CustomLinkButton
                                pageUrl={""}
                                link={Link}
                                isBtn={false}
                                btnClass={""}
                                linkClass={"btn btn-outline-primary mst-btn mst-show-more-btn"}
                                dir={dir}
                                linkIcon={true}
                                charLimit={40}
                            />
                        </div>

                    }

                </div>

            </article>
        </ComponentBackground>

    )

}


var elements = document.querySelectorAll('[id^="ContentShow-"]');


for (var i = 0; i < elements.length; i++) {


    var rootElement = document.getElementById(elements[i].id);
    var root = createRoot(rootElement);
    root.render(
        <ContentShow
            ComponentId={rootElement.getAttribute('component-id')}
            Title={rootElement.getAttribute('component-title')}
            Description={rootElement.getAttribute('component-description')}
            Query={rootElement.getAttribute('component-query')}
            CurrentPageId={rootElement.getAttribute('component-currentPageId')}
            BackColor={rootElement.getAttribute('component-backColor')}
            ContentShowType={rootElement.getAttribute('component-type')}
            TextFilter={rootElement.getAttribute('component-textFilter')}
            ShowFilters={rootElement.getAttribute('component-showFilters')}
            CollageLayoutSize={rootElement.getAttribute('component-layoutSize')}
            Link={rootElement.getAttribute('component-link')}
            ItemsNumberInRow={rootElement.getAttribute('component-itemstoshow')}
            HideShowMoreButton={rootElement.getAttribute('component-hideShowMoreButton')}
            LinkTarget={rootElement.getAttribute('component-linkTarget')}
            TitleAlignment={rootElement.getAttribute('component-titleAlignment')}
            ContentAlignment={rootElement.getAttribute('component-contentAlignment')}
        />);

}

